/**
 * Stripe-style prefixed ids (`usr_…`, `mbr_…`, `pcv_…`) as the newer Asclepius APIs render them: a resource
 * prefix plus the UUID's 16 bytes in unpadded base64url. The legacy APIs and the auth store still speak raw
 * UUIDs, so ids are converted at that boundary and nowhere else.
 */
export type PrefixedIdPrefix = "usr" | "mbr" | "pcv"

const UUID_HEX_LENGTH = 32
const UUID_BYTE_LENGTH = 16

export function toPrefixedId(prefix: PrefixedIdPrefix, uuid: string): string {
  const hex = uuid.replace(/-/g, "")
  if (hex.length !== UUID_HEX_LENGTH || !/^[0-9a-f]+$/i.test(hex)) {
    throw new Error(`Cannot build a '${prefix}_' id from '${uuid}': not a UUID`)
  }

  let binary = ""
  for (let index = 0; index < hex.length; index += 2) {
    binary += String.fromCharCode(parseInt(hex.slice(index, index + 2), 16))
  }
  const encoded = btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  return `${prefix}_${encoded}`
}

export function fromPrefixedId(prefixedId: string): string {
  const separator = prefixedId.indexOf("_")
  if (separator < 0) throw new Error(`'${prefixedId}' is not a prefixed id`)

  const encoded = prefixedId.slice(separator + 1)
  const padding = "=".repeat((4 - (encoded.length % 4)) % 4)
  const binary = atob(encoded.replace(/-/g, "+").replace(/_/g, "/") + padding)
  if (binary.length !== UUID_BYTE_LENGTH) throw new Error(`'${prefixedId}' does not encode a UUID`)

  const hex = Array.from(binary, (char) => char.charCodeAt(0).toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
